import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, StickyNote, UploadCloud, FileCheck2, AlarmClock, ListTodo, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api, downloadDocument } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { INTAKE_SECTIONS } from "../data/intakeSchema";
import { STAFF_FORMS } from "../data/staffFormsSchema";
import CaseFormDialog from "./CaseFormDialog";

const STATUS_COLORS = {
  RECEIVED: "bg-teal-light text-teal",
  "UNDER REVIEW": "bg-champagne-light text-champagne-dark",
  ACCEPTED: "bg-sage-light text-sage",
  "REPLACEMENT NEEDED": "bg-red-950/60 text-red-300",
  "TRANSLATION NEEDED": "bg-champagne-light text-champagne-dark",
  "APOSTILLE NEEDED": "bg-champagne-light text-champagne-dark",
  EXPIRED: "bg-red-950/60 text-red-300",
  "MISSING PAGE": "bg-red-950/60 text-red-300",
  REQUESTED: "bg-sand text-mutedink",
  SUBMITTED: "bg-teal-light text-teal",
  "NEEDS REPLACEMENT": "bg-red-950/60 text-red-300",
};

export default function StaffCaseDetail() {
  const { caseId } = useParams();
  const { user } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [options, setOptions] = useState(null);
  const [team, setTeam] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docRequests, setDocRequests] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [notices, setNotices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [caseForms, setCaseForms] = useState([]);
  const [formEditor, setFormEditor] = useState(null);
  const [docEditor, setDocEditor] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ description: "", fee_type: "professional", amount: "" });
  const [note, setNote] = useState("");
  const [receipts, setReceipts] = useState("");
  const [dialogs, setDialogs] = useState({ task: false, deadline: false, notice: false, docreq: false, upload: false, invoice: false });
  const [taskForm, setTaskForm] = useState({ title: "", due_date: "", notes: "" });
  const [deadlineForm, setDeadlineForm] = useState({ title: "", type: "General", date: "", notes: "" });
  const [noticeForm, setNoticeForm] = useState({ title: "", body: "", type: "General" });
  const [docreqItems, setDocreqItems] = useState("");
  const [uploadForm, setUploadForm] = useState({ file: null, name: "", designation: "VIEW ONLY", description: "" });

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/cases/${caseId}`);
      setCaseData(res.data);
      setReceipts((res.data.receipt_numbers || []).join("\n"));
      const uid = res.data.client_id;
      const [d, dr, t, dl, n, inv, sr, ci, cf] = await Promise.all([
        api.get("/documents", { params: { user_id: uid } }),
        api.get("/doc-requests", { params: { user_id: uid } }),
        api.get("/tasks", { params: { user_id: uid } }),
        api.get("/deadlines", { params: { user_id: uid } }),
        api.get("/notices", { params: { user_id: uid } }),
        api.get("/invoices", { params: { user_id: uid } }),
        api.get("/service-requests", { params: { user_id: uid } }),
        api.get("/client-intakes", { params: { user_id: uid } }),
        api.get("/case-forms", { params: { case_id: caseId } }),
      ]);
      setDocs(d.data);
      setDocRequests(dr.data);
      setTasks(t.data);
      setDeadlines(dl.data);
      setNotices(n.data);
      setInvoices(inv.data);
      setServiceRequests(sr.data);
      setIntakes(ci.data);
      setCaseForms(cf.data);
    } catch {
      toast.error("Could not load the case.");
    }
  }, [caseId]);

  useEffect(() => {
    load();
    api.get("/meta/options").then((r) => setOptions(r.data)).catch(() => {});
    api.get("/staff/team").then((r) => setTeam(r.data)).catch(() => {});
  }, [load]);

  const patchCase = async (payload) => {
    try {
      await api.patch(`/cases/${caseId}`, payload);
      toast.success("Case updated.");
      load();
    } catch {
      toast.error("Update failed.");
    }
  };

  const saveReceipts = () => {
    patchCase({ receipt_numbers: receipts.split("\n").map((r) => r.trim()).filter(Boolean) });
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/cases/${caseId}/notes`, { text: note });
      setNote("");
      load();
    } catch {
      toast.error("Could not add note.");
    }
  };

  const setJourney = async (step) => {
    try {
      await api.patch(`/staff/clients/${caseData.client_id}/journey`, { journey_step: step });
      toast.success("Journey step updated.");
      load();
    } catch {
      toast.error("Could not update journey.");
    }
  };

  const createTask = async () => {
    if (!taskForm.title) return toast.error("Task title required.");
    try {
      await api.post("/tasks", { ...taskForm, user_id: caseData.client_id, case_id: caseId });
      toast.success("Task assigned and client notified.");
      setDialogs({ ...dialogs, task: false });
      setTaskForm({ title: "", due_date: "", notes: "" });
      load();
    } catch { toast.error("Could not create task."); }
  };

  const createDeadline = async () => {
    if (!deadlineForm.title || !deadlineForm.date) return toast.error("Title and date required.");
    try {
      await api.post("/deadlines", { ...deadlineForm, user_id: caseData.client_id, case_id: caseId });
      toast.success("Deadline added and client notified.");
      setDialogs({ ...dialogs, deadline: false });
      setDeadlineForm({ title: "", type: "General", date: "", notes: "" });
      load();
    } catch { toast.error("Could not add deadline."); }
  };

  const createNotice = async () => {
    if (!noticeForm.title || !noticeForm.body) return toast.error("Title and message required.");
    try {
      await api.post("/notices", { ...noticeForm, user_id: caseData.client_id });
      toast.success("Notice posted to client portal.");
      setDialogs({ ...dialogs, notice: false });
      setNoticeForm({ title: "", body: "", type: "General" });
      load();
    } catch { toast.error("Could not post notice."); }
  };

  const createDocRequest = async () => {
    const items = docreqItems.split("\n").map((s) => s.trim()).filter(Boolean);
    if (items.length === 0) return toast.error("Add at least one item.");
    try {
      await api.post("/doc-requests", { user_id: caseData.client_id, case_id: caseId, items });
      toast.success("Document checklist sent to client.");
      setDialogs({ ...dialogs, docreq: false });
      setDocreqItems("");
      load();
    } catch { toast.error("Could not create request."); }
  };

  const staffUpload = async () => {
    if (!uploadForm.file || !uploadForm.name) return toast.error("Choose a file and name.");
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("name", uploadForm.name);
      fd.append("category", "Other");
      fd.append("case_id", caseId);
      fd.append("description", uploadForm.description);
      fd.append("designation", uploadForm.designation);
      fd.append("target_user_id", caseData.client_id);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document delivered to client portal.");
      setDialogs({ ...dialogs, upload: false });
      setUploadForm({ file: null, name: "", designation: "VIEW ONLY", description: "" });
      load();
    } catch { toast.error("Upload failed."); }
  };

  const setDocStatus = async (doc, status, noteText = "") => {
    try {
      await api.patch(`/documents/${doc.doc_id}`, { status, review_note: noteText });
      toast.success(`Document marked ${status}.`);
      load();
    } catch { toast.error("Could not update document."); }
  };

  const setRequestItem = async (reqId, itemId, status) => {
    const noteText = status === "NEEDS REPLACEMENT" ? window.prompt("Explain why a replacement is needed (shown to client):") || "" : "";
    try {
      await api.patch(`/doc-requests/${reqId}`, { item_id: itemId, status, note: noteText });
      load();
    } catch { toast.error("Could not update item."); }
  };

  const createInvoice = async () => {
    const amount = parseFloat(invoiceForm.amount);
    if (!invoiceForm.description || !amount || amount <= 0) return toast.error("Description and a valid amount are required.");
    try {
      await api.post("/invoices", { user_id: caseData.client_id, case_id: caseId, description: invoiceForm.description, fee_type: invoiceForm.fee_type, amount });
      toast.success("Invoice issued and client notified.");
      setDialogs({ ...dialogs, invoice: false });
      setInvoiceForm({ description: "", fee_type: "professional", amount: "" });
      load();
    } catch { toast.error("Could not create invoice."); }
  };

  const voidInvoice = async (inv) => {
    try {
      await api.patch(`/invoices/${inv.invoice_id}`, { status: "VOID" });
      toast.success("Invoice voided.");
      load();
    } catch { toast.error("Could not void invoice."); }
  };

  const setRequestStatus = async (req, status) => {
    try {
      await api.patch(`/service-requests/${req.request_id}`, { status });
      toast.success(`Request marked ${status}.`);
      load();
    } catch { toast.error("Could not update request."); }
  };

  const sendIntake = async () => {
    try {
      await api.post("/client-intakes/send", { user_id: caseData.client_id });
      toast.success("Intake sent to client portal.");
      load();
    } catch { toast.error("Could not send intake."); }
  };

  const reviewIntake = async (intake) => {
    try {
      await api.patch(`/client-intakes/${intake.intake_id}/review`);
      toast.success("Intake marked as reviewed.");
      load();
    } catch { toast.error("Could not update intake."); }
  };

  const renderAnswer = (field, value) => {
    if (value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) return null;
    if (field.type === "checks") return value.join(", ");
    if (field.type === "check") return value ? "Confirmed" : null;
    return String(value);
  };

  const latestIntake = intakes[0];

  if (!caseData || !options) {
    return <div className="py-16 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-teal border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="staff-case-detail">
      <Link to="/staff/cases" className="inline-flex items-center gap-1.5 text-sm text-mutedink hover:text-navy transition-colors" data-testid="back-to-cases">
        <ArrowLeft className="h-4 w-4" /> All cases
      </Link>

      <div className="bg-surface border border-line rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-serif font-semibold text-navy" data-testid="case-title">{caseData.title}</h1>
            <p className="text-sm text-mutedink mt-1" data-testid="case-client-name">
              {caseData.client?.name} · {caseData.client?.email}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Select value={caseData.status} onValueChange={(v) => patchCase({ status: v })}>
              <SelectTrigger className="w-56 bg-ivory" data-testid="case-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.case_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={caseData.assigned_staff || "unassigned"} onValueChange={(v) => patchCase({ assigned_staff: v === "unassigned" ? null : v })}>
              <SelectTrigger className="w-56 bg-ivory" data-testid="case-assign-select"><SelectValue placeholder="Assign staff" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {team.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <span className="text-xs text-mutedink">Client journey step:</span>
          <Select value={String(caseData.client?.journey_step ?? 0)} onValueChange={(v) => setJourney(Number(v))}>
            <SelectTrigger className="w-72 bg-ivory h-8 text-xs" data-testid="journey-step-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {options.journey_steps.map((s, i) => <SelectItem key={s} value={String(i)}>{i + 1}. {s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="overview" data-testid="case-tabs">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-surface border border-line p-1.5 rounded-xl">
          {["overview", "documents", "tasks", "deadlines", "notices", "payments", "requests", "forms", "notes"].map((t) => (
            <TabsTrigger key={t} value={t} className="text-xs capitalize data-[state=active]:bg-teal data-[state=active]:text-white rounded-lg" data-testid={`case-tab-${t}`}>{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="bg-surface border border-line rounded-2xl p-6">
            <h3 className="text-sm font-sans font-semibold text-navy mb-3">Government receipt numbers</h3>
            <Textarea rows={3} value={receipts} onChange={(e) => setReceipts(e.target.value)} placeholder="One receipt number per line" data-testid="receipts-input" />
            <Button onClick={saveReceipts} size="sm" className="mt-3 bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="save-receipts-button">Save receipts</Button>
          </div>
          <div className="bg-surface border border-line rounded-2xl p-6">
            <h3 className="text-sm font-sans font-semibold text-navy mb-2">Case details</h3>
            <dl className="text-sm space-y-2">
              <div className="flex justify-between"><dt className="text-mutedink">Service type</dt><dd className="text-ink font-medium">{caseData.service_type}</dd></div>
              <div className="flex justify-between"><dt className="text-mutedink">Opened</dt><dd className="text-ink">{new Date(caseData.created_at).toLocaleDateString()}</dd></div>
              <div className="flex justify-between"><dt className="text-mutedink">Last updated</dt><dd className="text-ink">{new Date(caseData.updated_at).toLocaleDateString()}</dd></div>
            </dl>
          </div>
          <div className="bg-surface border border-line rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-sans font-semibold text-navy">Client master intake</h3>
              {latestIntake ? (
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${["SUBMITTED", "REVIEWED"].includes(latestIntake.status) ? "bg-sage-light text-sage" : "bg-champagne-light text-champagne-dark"}`} data-testid="staff-intake-status">{latestIntake.status}</span>
              ) : (
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-sand text-mutedink">NOT SENT</span>
              )}
            </div>
            {!latestIntake ? (
              <div>
                <p className="text-sm text-mutedink mb-4">Send the secure Master Intake form so the client can complete it in their portal.</p>
                <Button onClick={sendIntake} size="sm" className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="send-intake-button">Send intake to client</Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="border-line text-navy hover:bg-sand rounded-full" data-testid="view-intake-button">View responses</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-surface border-line max-w-2xl max-h-[80vh] overflow-auto">
                    <DialogHeader><DialogTitle className="font-serif text-navy">Client intake responses</DialogTitle></DialogHeader>
                    <div className="space-y-6 mt-2" data-testid="intake-responses">
                      {INTAKE_SECTIONS.map((s) => {
                        const answered = s.fields.filter((f) => renderAnswer(f, latestIntake.answers?.[f.key]));
                        if (answered.length === 0) return null;
                        return (
                          <div key={s.id}>
                            <p className="text-xs font-bold tracking-[0.1em] uppercase text-teal mb-2">{s.title}</p>
                            <dl className="space-y-2">
                              {answered.map((f) => (
                                <div key={f.key} className="text-sm">
                                  <dt className="text-mutedink text-xs">{f.label}</dt>
                                  <dd className="text-ink whitespace-pre-wrap">{renderAnswer(f, latestIntake.answers[f.key])}</dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                        );
                      })}
                      {Object.keys(latestIntake.answers || {}).length === 0 && (
                        <p className="text-sm text-mutedink">The client has not started the form yet.</p>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
                {latestIntake.status === "SUBMITTED" && (
                  <Button onClick={() => reviewIntake(latestIntake)} size="sm" className="bg-sage text-white hover:bg-sage-dark rounded-full" data-testid="review-intake-button">Mark reviewed</Button>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-6 space-y-6">
          <div className="flex flex-wrap gap-3">
            <Dialog open={dialogs.upload} onOpenChange={(o) => setDialogs({ ...dialogs, upload: o })}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="staff-upload-button"><UploadCloud className="mr-2 h-4 w-4" /> Deliver document to client</Button>
              </DialogTrigger>
              <DialogContent className="bg-surface">
                <DialogHeader><DialogTitle className="font-serif text-navy">Deliver a document to the client</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                  <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.heic" onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] })} data-testid="staff-upload-file" />
                  <Input placeholder="Document name" value={uploadForm.name} onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })} data-testid="staff-upload-name" />
                  <Select value={uploadForm.designation} onValueChange={(v) => setUploadForm({ ...uploadForm, designation: v })}>
                    <SelectTrigger data-testid="staff-upload-designation"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["VIEW ONLY", "DOWNLOADABLE", "SIGNATURE REQUIRED", "ACTION REQUIRED"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Textarea rows={2} placeholder="Description (optional)" value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} data-testid="staff-upload-description" />
                  <Button onClick={staffUpload} className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="staff-upload-submit">Deliver securely</Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogs.docreq} onOpenChange={(o) => setDialogs({ ...dialogs, docreq: o })}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="border-line text-navy hover:bg-sand rounded-full" data-testid="create-docreq-button"><FileCheck2 className="mr-2 h-4 w-4" /> Request documents</Button>
              </DialogTrigger>
              <DialogContent className="bg-surface">
                <DialogHeader><DialogTitle className="font-serif text-navy">Document request checklist</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                  <Label>One item per line (e.g. "Passport bio page")</Label>
                  <Textarea rows={6} value={docreqItems} onChange={(e) => setDocreqItems(e.target.value)} placeholder={"Passport bio page\nBirth certificate\nCertified English translation\nI-94"} data-testid="docreq-items-input" />
                  <Button onClick={createDocRequest} className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="docreq-submit">Send checklist</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {docRequests.length > 0 && (
            <div className="bg-surface border border-line rounded-2xl p-6">
              <h3 className="text-sm font-sans font-semibold text-navy mb-4">Requested checklists</h3>
              {docRequests.map((req) => (
                <div key={req.request_id} className="mb-5 last:mb-0">
                  <ul className="space-y-2">
                    {req.items.map((item) => (
                      <li key={item.item_id} className="flex flex-wrap items-center justify-between gap-2 bg-ivory rounded-lg px-4 py-3" data-testid={`staff-docreq-item-${item.item_id}`}>
                        <div className="flex items-center gap-3">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[item.status] || "bg-sand text-mutedink"}`}>{item.status}</span>
                          <span className="text-sm text-ink">{item.name}</span>
                          {item.doc_id && (
                            <button onClick={() => downloadDocument(item.doc_id, item.name)} className="text-xs text-teal hover:underline" data-testid={`staff-docreq-view-${item.item_id}`}>View</button>
                          )}
                        </div>
                        {item.status === "SUBMITTED" && (
                          <div className="flex gap-2">
                            <button onClick={() => setRequestItem(req.request_id, item.item_id, "ACCEPTED")} className="text-xs font-semibold text-sage hover:underline" data-testid={`docreq-accept-${item.item_id}`}>Accept</button>
                            <button onClick={() => setRequestItem(req.request_id, item.item_id, "NEEDS REPLACEMENT")} className="text-xs font-semibold text-red-600 hover:underline" data-testid={`docreq-reject-${item.item_id}`}>Needs replacement</button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <div className="bg-surface border border-line rounded-2xl p-6">
            <h3 className="text-sm font-sans font-semibold text-navy mb-4">Client documents ({docs.length})</h3>
            {docs.length === 0 ? (
              <p className="text-sm text-mutedink">No documents uploaded yet.</p>
            ) : (
              <div className="space-y-3">
                {docs.map((d) => (
                  <div key={d.doc_id} className="flex flex-col lg:flex-row lg:items-center gap-3 border border-line rounded-xl p-4" data-testid={`staff-doc-row-${d.doc_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy">{d.name}</p>
                      <p className="text-xs text-mutedink">{d.category} · uploaded by {d.uploaded_by_name} · {new Date(d.created_at).toLocaleDateString()}{d.designation && ` · ${d.designation}`}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button onClick={() => downloadDocument(d.doc_id, d.original_filename)} className="text-xs font-semibold text-teal hover:underline" data-testid={`staff-doc-download-${d.doc_id}`}>Download</button>
                      <button onClick={() => setDocEditor({ doc: d, name: d.name, category: d.category })} className="text-xs font-semibold text-mutedink hover:text-teal" data-testid={`staff-doc-edit-${d.doc_id}`}>Rename / category</button>
                      <Select value={d.status} onValueChange={(v) => setDocStatus(d, v)}>
                        <SelectTrigger className="w-44 h-8 text-xs" data-testid={`staff-doc-status-${d.doc_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {options.doc_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Rename / recategorize document */}
          <Dialog open={!!docEditor} onOpenChange={(o) => !o && setDocEditor(null)}>
            <DialogContent className="bg-surface border-line max-w-md" data-testid="doc-edit-dialog">
              <DialogHeader><DialogTitle className="font-serif text-navy">Rename / recategorize document</DialogTitle></DialogHeader>
              {docEditor && (
                <div className="space-y-4 mt-2">
                  <div>
                    <Label>Document name</Label>
                    <Input value={docEditor.name} onChange={(e) => setDocEditor({ ...docEditor, name: e.target.value })} className="mt-1.5" data-testid="doc-edit-name" />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={docEditor.category} onValueChange={(v) => setDocEditor({ ...docEditor, category: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="doc-edit-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {options.doc_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        await api.patch(`/documents/${docEditor.doc.doc_id}`, { name: docEditor.name, category: docEditor.category });
                        toast.success("Document updated.");
                        setDocEditor(null);
                        load();
                      } catch { toast.error("Could not update document."); }
                    }}
                    className="bg-teal text-white hover:bg-teal-dark rounded-full"
                    data-testid="doc-edit-save"
                  >
                    Save changes
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="tasks" className="mt-6 space-y-4">
          <Dialog open={dialogs.task} onOpenChange={(o) => setDialogs({ ...dialogs, task: o })}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="create-task-button"><ListTodo className="mr-2 h-4 w-4" /> Assign task</Button>
            </DialogTrigger>
            <DialogContent className="bg-surface">
              <DialogHeader><DialogTitle className="font-serif text-navy">Assign a task to the client</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <Input placeholder="e.g. Upload 2025 tax return" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} data-testid="task-title-input" />
                <div>
                  <Label>Due date</Label>
                  <Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} className="mt-1.5" data-testid="task-due-input" />
                </div>
                <Textarea rows={2} placeholder="Notes (optional)" value={taskForm.notes} onChange={(e) => setTaskForm({ ...taskForm, notes: e.target.value })} data-testid="task-notes-input" />
                <Button onClick={createTask} className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="task-submit">Assign &amp; notify client</Button>
              </div>
            </DialogContent>
          </Dialog>
          {tasks.length === 0 ? (
            <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No tasks assigned yet.</p>
          ) : (
            <div className="bg-surface border border-line rounded-2xl divide-y divide-line">
              {tasks.map((t) => (
                <div key={t.task_id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`staff-task-row-${t.task_id}`}>
                  <div>
                    <p className="text-sm font-medium text-navy">{t.title}</p>
                    <p className="text-xs text-mutedink">{t.due_date && `Due ${t.due_date} · `}by {t.created_by}</p>
                  </div>
                  <Select value={t.status} onValueChange={async (v) => { await api.patch(`/tasks/${t.task_id}`, { status: v }); load(); }}>
                    <SelectTrigger className="w-52 h-8 text-xs" data-testid={`staff-task-status-${t.task_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {options.task_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="deadlines" className="mt-6 space-y-4">
          <Dialog open={dialogs.deadline} onOpenChange={(o) => setDialogs({ ...dialogs, deadline: o })}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="create-deadline-button"><AlarmClock className="mr-2 h-4 w-4" /> Add deadline</Button>
            </DialogTrigger>
            <DialogContent className="bg-surface">
              <DialogHeader><DialogTitle className="font-serif text-navy">Add an important date</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <Input placeholder="e.g. USCIS RFE response due" value={deadlineForm.title} onChange={(e) => setDeadlineForm({ ...deadlineForm, title: e.target.value })} data-testid="deadline-title-input" />
                <Select value={deadlineForm.type} onValueChange={(v) => setDeadlineForm({ ...deadlineForm, type: v })}>
                  <SelectTrigger data-testid="deadline-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.deadline_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={deadlineForm.date} onChange={(e) => setDeadlineForm({ ...deadlineForm, date: e.target.value })} className="mt-1.5" data-testid="deadline-date-input" />
                </div>
                <Textarea rows={2} placeholder="Notes (optional)" value={deadlineForm.notes} onChange={(e) => setDeadlineForm({ ...deadlineForm, notes: e.target.value })} data-testid="deadline-notes-input" />
                <Button onClick={createDeadline} className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="deadline-submit">Add &amp; notify client</Button>
              </div>
            </DialogContent>
          </Dialog>
          {deadlines.length === 0 ? (
            <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No deadlines recorded.</p>
          ) : (
            <div className="bg-surface border border-line rounded-2xl divide-y divide-line">
              {deadlines.map((d) => (
                <div key={d.deadline_id} className="p-4 flex items-center justify-between gap-3" data-testid={`staff-deadline-row-${d.deadline_id}`}>
                  <div>
                    <p className="text-sm font-medium text-navy">{d.title}</p>
                    <p className="text-xs text-mutedink">{d.type}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-navy">{d.date}</span>
                    <button onClick={async () => { await api.delete(`/deadlines/${d.deadline_id}`); load(); }} className="text-xs text-red-600 hover:underline" data-testid={`deadline-delete-${d.deadline_id}`}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="notices" className="mt-6 space-y-4">
          <Dialog open={dialogs.notice} onOpenChange={(o) => setDialogs({ ...dialogs, notice: o })}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="create-notice-button"><BellRing className="mr-2 h-4 w-4" /> Post notice</Button>
            </DialogTrigger>
            <DialogContent className="bg-surface">
              <DialogHeader><DialogTitle className="font-serif text-navy">Post a notice to the client portal</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <Input placeholder="Title" value={noticeForm.title} onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })} data-testid="notice-title-input" />
                <Select value={noticeForm.type} onValueChange={(v) => setNoticeForm({ ...noticeForm, type: v })}>
                  <SelectTrigger data-testid="notice-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["General", "USCIS", "Immigration Court", "Payment", "Appointment"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea rows={4} placeholder="Message" value={noticeForm.body} onChange={(e) => setNoticeForm({ ...noticeForm, body: e.target.value })} data-testid="notice-body-input" />
                <Button onClick={createNotice} className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="notice-submit">Post &amp; notify client</Button>
              </div>
            </DialogContent>
          </Dialog>
          {notices.length === 0 ? (
            <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No notices posted.</p>
          ) : (
            <div className="space-y-3">
              {notices.map((n) => (
                <div key={n.notice_id} className="bg-surface border border-line rounded-xl p-5" data-testid={`staff-notice-${n.notice_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-semibold text-navy">{n.title}</p>
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-teal-light text-teal">{n.type}</span>
                  </div>
                  <p className="text-sm text-mutedink whitespace-pre-wrap">{n.body}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="payments" className="mt-6 space-y-4">
          <Dialog open={dialogs.invoice} onOpenChange={(o) => setDialogs({ ...dialogs, invoice: o })}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="create-invoice-button">Issue invoice</Button>
            </DialogTrigger>
            <DialogContent className="bg-surface border-line">
              <DialogHeader><DialogTitle className="font-serif text-navy">Issue an invoice</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <Input placeholder="Description (e.g. I-130 preparation — stage 1)" value={invoiceForm.description} onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })} data-testid="invoice-description-input" />
                <div>
                  <Label>Fee type (who receives the money)</Label>
                  <Select value={invoiceForm.fee_type} onValueChange={(v) => setInvoiceForm({ ...invoiceForm, fee_type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="invoice-fee-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {options.fee_types.map((f) => <SelectItem key={f.value} value={f.value}>{f.label} — {f.recipient}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Amount (USD)</Label>
                  <Input type="number" min="0" step="0.01" placeholder="250.00" value={invoiceForm.amount} onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })} className="mt-1.5" data-testid="invoice-amount-input" />
                </div>
                <Button onClick={createInvoice} className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="invoice-submit">Issue &amp; notify client</Button>
              </div>
            </DialogContent>
          </Dialog>
          {invoices.length === 0 ? (
            <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No invoices for this client yet.</p>
          ) : (
            <div className="bg-surface border border-line rounded-2xl divide-y divide-line">
              {invoices.map((inv) => (
                <div key={inv.invoice_id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`staff-invoice-row-${inv.invoice_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy">{inv.description} <span className="text-xs text-mutedink">· ${inv.amount.toFixed(2)}</span></p>
                    <p className="text-xs text-mutedink">{inv.fee_label} · to {inv.recipient} · {new Date(inv.created_at).toLocaleDateString()}{inv.paid_at && ` · paid ${new Date(inv.paid_at).toLocaleDateString()}`}</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full w-fit ${inv.status === "PAID" ? "bg-sage-light text-sage" : inv.status === "VOID" ? "bg-red-950/60 text-red-300" : "bg-champagne-light text-champagne-dark"}`}>{inv.status}</span>
                  {inv.status === "OPEN" && (
                    <button onClick={() => voidInvoice(inv)} className="text-xs text-red-300 hover:underline" data-testid={`invoice-void-${inv.invoice_id}`}>Void</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests" className="mt-6 space-y-4">
          {serviceRequests.length === 0 ? (
            <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No service requests from this client yet.</p>
          ) : (
            <div className="bg-surface border border-line rounded-2xl divide-y divide-line">
              {serviceRequests.map((r) => (
                <div key={r.request_id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`staff-request-row-${r.request_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy">{r.service_type}{r.urgency === "urgent" && <span className="text-champagne-dark text-xs font-bold"> · URGENT</span>}</p>
                    <p className="text-xs text-mutedink">{new Date(r.created_at).toLocaleDateString()}{r.preferred_mode && ` · ${r.preferred_mode}`}{r.details && ` · ${r.details}`}</p>
                  </div>
                  <Select value={r.status} onValueChange={(v) => setRequestStatus(r, v)}>
                    <SelectTrigger className="w-44 h-8 text-xs" data-testid={`staff-request-status-${r.request_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {options.request_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="forms" className="mt-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            {STAFF_FORMS.map((f) => (
              <Button key={f.id} size="sm" variant="outline" onClick={() => setFormEditor({ formType: f.id, existing: null })} className="border-line text-navy hover:bg-sand rounded-full text-xs" data-testid={`case-add-form-${f.id}`}>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> {f.id} {f.title}
              </Button>
            ))}
          </div>
          {caseForms.length === 0 ? (
            <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No case forms yet. Use the buttons above to fill one for this case.</p>
          ) : (
            <div className="bg-surface border border-line rounded-2xl divide-y divide-line">
              {caseForms.map((f) => {
                const meta = STAFF_FORMS.find((x) => x.id === f.form_type);
                return (
                  <div key={f.form_id} className="p-4 flex items-center justify-between gap-3" data-testid={`case-form-row-${f.form_id}`}>
                    <div>
                      <p className="text-sm font-medium text-navy">{f.form_type} — {meta?.title || "Form"}</p>
                      <p className="text-xs text-mutedink">by {f.filled_by_name} · updated {new Date(f.updated_at).toLocaleString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setFormEditor({ formType: f.form_type, existing: f })} className="text-xs font-semibold text-teal hover:underline" data-testid={`case-form-edit-${f.form_id}`}>Open</button>
                      <button onClick={async () => { await api.delete(`/case-forms/${f.form_id}`); load(); }} className="text-xs text-red-300 hover:underline" data-testid={`case-form-delete-${f.form_id}`}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {formEditor && (
            <CaseFormDialog
              open={!!formEditor}
              onOpenChange={(o) => !o && setFormEditor(null)}
              formType={formEditor.formType}
              caseId={caseId}
              existing={formEditor.existing}
              onSaved={load}
            />
          )}
        </TabsContent>

        <TabsContent value="notes" className="mt-6 space-y-4">
          <div className="bg-surface border border-line rounded-2xl p-6">
            <h3 className="text-sm font-sans font-semibold text-navy mb-3 flex items-center gap-2"><StickyNote className="h-4 w-4 text-teal" /> Staff notes (internal only)</h3>
            <div className="flex gap-3">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a case note..." data-testid="case-note-input" />
              <Button onClick={addNote} className="bg-teal text-white hover:bg-teal-dark rounded-full self-end" data-testid="add-note-button"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="space-y-3">
            {(caseData.notes || []).slice().reverse().map((n) => (
              <div key={n.note_id} className="bg-surface border border-line rounded-xl p-4" data-testid={`case-note-${n.note_id}`}>
                <p className="text-sm text-ink whitespace-pre-wrap">{n.text}</p>
                <p className="text-xs text-mutedink mt-2">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
              </div>
            ))}
            {(caseData.notes || []).length === 0 && <p className="text-sm text-mutedink">No notes yet.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
